import type { RequestFn } from '../http';
import type { AuthSession, UiPreferences, User } from '../types';

/** Eine Farbe zur Selbstwahl — `by`: wer sie (`taken`) bzw. die ähnliche (`similar`) trägt; `reserved` wählen nur Admins */
export type MyColorOption = {
  color: string;
  status: 'own' | 'free' | 'taken' | 'similar' | 'reserved';
  by: string[];
};

/** Nur gesetzt, wenn NODE_ENV=development am Backend (hilft beim lokalen Test mit Mailpit). */
export type MagicLinkRequestResult = {
  message: string;
  devMailStatus?:
    | 'skipped_no_user'
    | 'skipped_inactive'
    | 'skipped_not_local_admin'
    | 'sent';
};

export type MicrosoftSsoStartResult = {
  sessionId: string;
  verificationUriComplete: string;
  verificationUri: string;
  userCode: string;
  expiresIn: number;
  pollIntervalMs: number;
};

export type LoginOptionsResult = {
  ssoEnabled: boolean;
  localAdminEmail: string;
};

export type MicrosoftSsoPollResult =
  | { status: 'pending'; pollIntervalMs: number }
  | { status: 'error'; message: string }
  | {
      status: 'ok';
      user: Pick<User, 'id' | 'email' | 'displayName' | 'role' | 'cwMemberIdentifier'>;
    }
  | {
      status: 'ok';
      testOnly: true;
      microsoftEmail: string;
      microsoftName: string | null;
    };

export function createAuthApi(request: RequestFn) {
  return {
    async getLoginOptions() {
      return request<LoginOptionsResult>('/auth/login-options');
    },

    async requestMagicLink(email: string, redirect?: string) {
      return request<MagicLinkRequestResult>('/auth/magic-link/request', {
        method: 'POST',
        body: JSON.stringify({ email, ...(redirect ? { redirect } : {}) }),
      });
    },

    async logout() {
      return request<{ success: boolean }>('/auth/logout', {
        method: 'POST',
      });
    },

    async getMe() {
      // hasWeclappApiToken: nur der Gesetzt-Status — der Token selbst verlässt den Server nie.
      return request<{ user: User & { hasWeclappApiToken?: boolean } }>('/auth/me');
    },

    /** Persönlichen weclapp-API-Token hinterlegen (verschlüsselt gespeichert). */
    async setWeclappApiToken(token: string) {
      return request<{ hasWeclappApiToken: boolean }>('/auth/me/weclapp-token', {
        method: 'PUT',
        body: JSON.stringify({ token }),
      });
    },

    async deleteWeclappApiToken() {
      return request<{ hasWeclappApiToken: boolean }>('/auth/me/weclapp-token', {
        method: 'DELETE',
      });
    },

    /** Eigene Personenfarbe: welche frei sind (vergeben/zu ähnlich = gesperrt). */
    async getMyColorOptions() {
      return request<{ options: MyColorOption[] }>('/auth/me/color');
    },

    /** Eigene Personenfarbe setzen — 409 `COLOR_UNAVAILABLE`, wenn sie inzwischen jemand hat. */
    async setMyColor(color: string) {
      return request<{ color: string }>('/auth/me/color', {
        method: 'PUT',
        body: JSON.stringify({ color }),
      });
    },

    async updateMySettings(settings: { defaultDepartureLocation?: string; phoneLandline?: string | null; phoneMobile?: string | null }) {
      return request<{ user: User }>('/auth/me/settings', {
        method: 'PATCH',
        body: JSON.stringify(settings),
      });
    },

    /** Persönliche UI-Einstellungen (Menü-Favoriten etc.) speichern. */
    async updateMyPreferences(uiPreferences: UiPreferences) {
      return request<{ user: User }>('/auth/me/settings', {
        method: 'PATCH',
        body: JSON.stringify({ uiPreferences }),
      });
    },

    async getAuthSessions() {
      return request<{ sessions: AuthSession[] }>('/auth/sessions');
    },

    async revokeSession(sessionId: string) {
      return request<{ success: boolean }>(`/auth/sessions/${encodeURIComponent(sessionId)}`, {
        method: 'DELETE',
      });
    },

    async revokeOtherSessions() {
      return request<{ success: boolean }>('/auth/sessions', {
        method: 'DELETE',
      });
    },

    async startMicrosoftSso(options?: { testOnly?: boolean }) {
      return request<MicrosoftSsoStartResult>('/auth/sso/microsoft/start', {
        method: 'POST',
        body: JSON.stringify(options ?? {}),
      });
    },

    async pollMicrosoftSso(sessionId: string) {
      const q = new URLSearchParams({ sessionId });
      return request<MicrosoftSsoPollResult>(`/auth/sso/microsoft/poll?${q.toString()}`);
    },

    // ── Heimatadresse: Selbstpflege mit Admin-Freigabe ──────────────────────────
    async getHomeAddress() {
      return request<HomeAddressState>('/profile/home-address');
    },

    async submitHomeAddress(data: {
      street: string;
      postalCode: string;
      city: string;
      note?: string;
    }) {
      return request<{ request: HomeAddressChangeRequest }>('/profile/home-address', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async withdrawHomeAddress() {
      return request<{ success: boolean }>('/profile/home-address', { method: 'DELETE' });
    },

    async getHomeAddressRequests(status?: 'PENDING' | 'APPROVED' | 'REJECTED' | 'ALL') {
      const qs = status ? `?status=${status}` : '';
      return request<{ requests: HomeAddressChangeRequest[]; pendingCount: number }>(
        `/admin/home-address-requests${qs}`,
      );
    },

    async reviewHomeAddressRequest(
      id: string,
      data: { status: 'APPROVED' | 'REJECTED'; reviewNote?: string },
    ) {
      return request<{ request: HomeAddressChangeRequest }>(
        `/admin/home-address-requests/${encodeURIComponent(id)}`,
        { method: 'PATCH', body: JSON.stringify(data) },
      );
    },
  };
}

export interface HomeAddressChangeRequest {
  id: string;
  street: string;
  postalCode: string;
  city: string;
  previous: { street: string | null; postalCode: string | null; city: string | null } | null;
  note: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: string;
  reviewedAt: string | null;
  reviewNote: string | null;
  user?: { id: string; displayName: string; email: string };
  reviewedBy?: { id: string; displayName: string } | null;
}

export interface HomeAddressState {
  /** Freigegebener Stand — nur dieser zählt für die KM-Abrechnung */
  approved: { street: string; postalCode: string | null; city: string | null } | null;
  pending: HomeAddressChangeRequest | null;
  lastDecision: HomeAddressChangeRequest | null;
}
